/* ===== components/dashboard/UserList.jsx ===== */
import React, { useState } from 'react';
import { Table, Spin, Modal, Form, Input, Button, message, Badge } from 'antd';
import { gql, useQuery, useMutation, useSubscription } from '@apollo/client';
import { ALL_USERS_ONLINE_STATUS_SUBSCRIPTION } from '../../graphql/subscriptions';

// Запрос списка пользователей
const GET_USERS = gql`
  query GetAllUsers {
    allUsers {
      id
      firstName
      lastName
      email
      roles {
        id
        name
      }
    }
  }
`;

// Мутация обновления пользователя
const UPDATE_USER = gql`
  mutation UpdateUser(
    $userId: String!
    $firstName: String
    $lastName: String
    $email: String
  ) {
    updateUser(
      userId: $userId
      firstName: $firstName
      lastName: $lastName
      email: $email
    ) {
      id
      firstName
      lastName
      email
    }
  }
`;

// Мутация удаления пользователя
const DELETE_USER = gql`
  mutation DeleteUser($userId: String!) {
    deleteUser(userId: $userId)
  }
`;

const UserList = () => {
  const { loading, error, data } = useQuery(GET_USERS);
  const [updateUser] = useMutation(UPDATE_USER, { refetchQueries: [{ query: GET_USERS }] });
  const [deleteUser] = useMutation(DELETE_USER, { refetchQueries: [{ query: GET_USERS }] });

  const [onlineMap, setOnlineMap] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();

  // Подписка на статусы всех пользователей
  useSubscription(ALL_USERS_ONLINE_STATUS_SUBSCRIPTION, {
    onSubscriptionData: ({ subscriptionData }) => {
      if (!subscriptionData.data) return;
      const { userId, isOnline } = subscriptionData.data.allUsersOnlineStatusChanged;
      setOnlineMap(prev => ({ ...prev, [userId]: isOnline }));
    },
    onError: (err) => console.error('Subscription error:', err),
  });

  if (loading) return <Spin />;
  if (error) return <p>Ошибка: {error.message}</p>;

  const users = data.allUsers;

  const handleEdit = (user) => {
    setSelectedUser(user);
    form.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    });
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await updateUser({ variables: { userId: selectedUser.id, ...values } });
      message.success('Пользователь успешно обновлен');
      setIsModalVisible(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Update error:', err);
      message.error('Ошибка при обновлении пользователя');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser({ variables: { userId: id } });
      message.success('Пользователь успешно удалён');
    } catch (err) {
      console.error('Delete error:', err);
      message.error('Ошибка при удалении пользователя');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    {
      title: 'Пользователь', key: 'user',
      render: (_, record) => (
        <Badge
          status={onlineMap[record.id] ? 'success' : 'default'}
          text={`${record.firstName} ${record.lastName}`}
        />
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Роли', dataIndex: 'roles', key: 'roles',
      render: roles => roles.map(r => r.name).join(', ') || '—'
    },
    {
      title: 'Действия', key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEdit(record)}>Редактировать</Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>Удалить</Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Table dataSource={users} columns={columns} rowKey="id" />
      <Modal
        title="Редактировать пользователя"
        visible={isModalVisible}
        onOk={handleSave}
        onCancel={() => { setIsModalVisible(false); setSelectedUser(null); }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="firstName" label="Имя" rules={[{ required: true, message: 'Введите имя' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label="Фамилия" rules={[{ required: true, message: 'Введите фамилию' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Введите email' }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UserList;
